import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import {
  adminPurchasesList,
  adminResetPurchaseReportRegenerations,
  adminStats,
  adminUsersList,
  apiBase,
  fetchComforttoolConfig,
  type ComforttoolConfig,
} from '../lib/api';
import { COMFORT_ENGINE_VERSION } from '../lib/comfortCalc';
import { purchaseBillingLabelNl, STRIPE_WEBHOOK_EVENTS } from '../lib/purchaseBilling';

type AdminPurchaseRow = {
  id: number;
  user_id: number;
  user_email: string;
  stripe_checkout_session_id: string;
  bundle_tier: string;
  created_at: number;
  report_regenerations_used: number;
  billing_status?: string;
};

type AdminUserRow = { id: number; email: string; is_superadmin: number; created_at: number };

function StatusPill({ ok }: { ok: boolean }) {
  return (
    <span
      className={
        ok
          ? 'rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-medium text-emerald-300'
          : 'rounded-full bg-ct-warn/15 px-2 py-0.5 text-xs font-medium text-ct-warn'
      }
    >
      {ok ? 'actief' : 'ontbreekt'}
    </span>
  );
}

export function AdminPage() {
  const { user, token, loading } = useAuth();
  const [forbidden, setForbidden] = useState(false);
  const [stats, setStats] = useState<{ users: number; purchases: number; pending_checkouts: number } | null>(null);
  const [purchases, setPurchases] = useState<AdminPurchaseRow[]>([]);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [resetBusyId, setResetBusyId] = useState<number | null>(null);
  const [integration, setIntegration] = useState<ComforttoolConfig | null>(null);

  useEffect(() => {
    if (!token || !user?.is_superadmin) return;
    let c = false;
    (async () => {
      const cfg = await fetchComforttoolConfig();
      if (c) return;
      setIntegration(cfg);
      const s = await adminStats(token);
      if (c) return;
      if (!s.ok) {
        setForbidden(true);
        return;
      }
      setStats(s.stats);
      const p = await adminPurchasesList(token, 50, 0);
      const u = await adminUsersList(token, 50, 0);
      if (c) return;
      if (p.ok) setPurchases(p.purchases);
      if (u.ok) setUsers(u.users);
    })();
    return () => {
      c = true;
    };
  }, [token, user]);

  async function resetReportRegenerations(purchaseId: number) {
    if (!token) return;
    setResetBusyId(purchaseId);
    try {
      const r = await adminResetPurchaseReportRegenerations(token, purchaseId);
      if (!r.ok) {
        window.alert(r.message ?? 'Mislukt');
        return;
      }
      setPurchases((prev) =>
        prev.map((row) => (row.id === purchaseId ? { ...row, report_regenerations_used: 0 } : row))
      );
    } finally {
      setResetBusyId(null);
    }
  }

  if (!loading && !user) {
    return <Navigate to="/inloggen" replace />;
  }
  if (!loading && user && !user.is_superadmin) {
    return <Navigate to="/account" replace />;
  }
  if (forbidden) {
    return <Navigate to="/account" replace />;
  }

  return (
    <>
      <Seo path="/admin" title="Admin" description="ComfortTool superadmin dashboard." noindex />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <Link to="/" className="text-sm text-ct-accent hover:underline">
          ← Home
        </Link>
        <h1 className="mt-4 text-2xl font-bold text-ct-text">Superadmin</h1>
        <p className="mt-1 font-mono text-xs text-ct-muted">Rekenkern {COMFORT_ENGINE_VERSION}</p>
        {integration && (
          <section
            className="mt-6 rounded-xl border border-ct-border bg-ct-surface p-5"
            aria-label="Status koppelingen beheer"
          >
            <h2 className="text-sm font-semibold text-ct-text">Online betalen &amp; diensten (beheer)</h2>
            <p className="mt-1 text-xs text-ct-muted">
              Publieke status van <code className="text-ct-accent">{apiBase()}</code>. Secrets zet je in Cloudflare (Workers
              → Variables) of via <code className="text-ct-accent">wrangler secret put</code>; zie{' '}
              <code className="text-ct-accent">worker/wrangler.toml</code>.
            </p>
            <div className="mt-4 rounded-lg border border-ct-border bg-ct-bg/80 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ct-muted">Stripe-webhook (verplicht)</h3>
              <p className="mt-2 text-xs text-ct-muted">
                Endpoint-URL in Stripe Dashboard → Developers → Webhooks:
              </p>
              <code className="mt-1 block break-all rounded bg-ct-elevated px-2 py-1.5 text-xs text-ct-accent">
                {apiBase()}/webhooks/stripe
              </code>
              <p className="mt-3 text-xs font-medium text-ct-text">Selecteer deze events (alle zes):</p>
              <ul className="mt-2 space-y-2 text-xs text-ct-muted">
                {STRIPE_WEBHOOK_EVENTS.map((ev) => (
                  <li key={ev.type} className="border-l-2 border-ct-accent/40 pl-3">
                    <code className="text-ct-text">{ev.type}</code>
                    <span className="mt-0.5 block text-ct-muted">{ev.description}</span>
                  </li>
                ))}
              </ul>
            </div>
            <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <li className="flex items-center justify-between gap-2 rounded-lg bg-ct-elevated/50 px-3 py-2">
                <span className="text-ct-muted">Online betalen beschikbaar</span>
                <StatusPill ok={integration.stripe_configured === true} />
              </li>
              <li className="flex items-center justify-between gap-2 rounded-lg bg-ct-elevated/50 px-3 py-2">
                <span className="text-ct-muted">Bevestiging na betaling (webhook)</span>
                <StatusPill ok={integration.stripe_webhook_configured === true} />
              </li>
              <li className="flex items-center justify-between gap-2 rounded-lg bg-ct-elevated/50 px-3 py-2">
                <span className="text-ct-muted">Wachtwoord-resetmail</span>
                <StatusPill ok={integration.password_reset_mail_configured === true} />
              </li>
              <li className="flex items-center justify-between gap-2 rounded-lg bg-ct-elevated/50 px-3 py-2">
                <span className="text-ct-muted">Uitgebreid leesrapport (Comfort Plus)</span>
                <StatusPill ok={integration.report_available === true} />
              </li>
              <li className="flex items-center justify-between gap-2 rounded-lg bg-ct-elevated/50 px-3 py-2">
                <span className="text-ct-muted">Installatieadviseur (chat) ingeschakeld</span>
                <StatusPill ok={integration.support_desk_available === true} />
              </li>
            </ul>
          </section>
        )}
        {stats && (
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-ct-border bg-ct-surface p-4">
              <p className="text-sm text-ct-muted">Gebruikers</p>
              <p className="text-2xl font-bold text-ct-text">{stats.users}</p>
            </div>
            <div className="rounded-xl border border-ct-border bg-ct-surface p-4">
              <p className="text-sm text-ct-muted">Aankopen</p>
              <p className="text-2xl font-bold text-ct-text">{stats.purchases}</p>
            </div>
            <div className="rounded-xl border border-ct-border bg-ct-surface p-4">
              <p className="text-sm text-ct-muted">Betalingen niet afgerond</p>
              <p className="text-2xl font-bold text-ct-text">{stats.pending_checkouts}</p>
            </div>
          </div>
        )}
        <h2 className="mt-10 text-lg font-semibold text-ct-text">Laatste aankopen</h2>
        <div className="mt-4 overflow-x-auto rounded-xl border border-ct-border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-ct-border bg-ct-elevated text-ct-muted">
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">E-mail</th>
                <th className="px-3 py-2">Bundel</th>
                <th className="px-3 py-2">Betaling</th>
                <th className="px-3 py-2">Rapport-teller</th>
                <th className="px-3 py-2">Betaalreferentie</th>
                <th className="px-3 py-2">Actie</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((row) => (
                <tr key={row.id} className="border-b border-ct-border/60">
                  <td className="px-3 py-2 tabular-nums">{row.id}</td>
                  <td className="px-3 py-2">{row.user_email}</td>
                  <td className="px-3 py-2">{row.bundle_tier}</td>
                  <td className="px-3 py-2 text-xs text-ct-muted">
                    {purchaseBillingLabelNl(row.billing_status ?? 'active')}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{row.report_regenerations_used}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.stripe_checkout_session_id.slice(0, 24)}…</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      disabled={resetBusyId === row.id}
                      onClick={() => void resetReportRegenerations(row.id)}
                      className="rounded-lg border border-ct-border px-2 py-1 text-xs font-medium text-ct-text hover:bg-ct-elevated disabled:opacity-50"
                    >
                      {resetBusyId === row.id ? '…' : 'Reset teller'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <h2 className="mt-10 text-lg font-semibold text-ct-text">Gebruikers</h2>
        <div className="mt-4 overflow-x-auto rounded-xl border border-ct-border">
          <table className="w-full min-w-[400px] text-left text-sm">
            <thead>
              <tr className="border-b border-ct-border bg-ct-elevated text-ct-muted">
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">E-mail</th>
                <th className="px-3 py-2">Superadmin</th>
              </tr>
            </thead>
            <tbody>
              {users.map((row) => (
                <tr key={row.id} className="border-b border-ct-border/60">
                  <td className="px-3 py-2 tabular-nums">{row.id}</td>
                  <td className="px-3 py-2">{row.email}</td>
                  <td className="px-3 py-2">{row.is_superadmin ? 'ja' : 'nee'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
